/**
 * useFlowOptimizerOverlay — surfaces TrafficPulse optimizer preview rows
 * on the flow detail page when the campaign has the optimizer enabled for
 * this exact flow.
 *
 * Returns `enabled: false` (and an empty `items` list) whenever:
 *   - no campaign/flow id is known yet,
 *   - the campaign has no optimizer config,
 *   - the optimizer is disabled or kill-switched,
 *   - the optimizer targets a different flow than the one being viewed.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';
import type {
  FlowOptimizerItemPreview,
  TagTrafficProvider,
} from '@trafficpulse/shared';

import {
  getConfig,
  previewOptimizer,
  type FlowOptimizerProvider,
} from '@/features/flow-optimizer/services/flow-optimizer-api';
import { tagProviderOf } from '@/features/tag-rule-scope/model/tag-provider';

export interface UseFlowOptimizerOverlayParams {
  flowId: string;
  campaignId?: string;
  provider?: FlowOptimizerProvider;
  /** Dev-only: fetch preview even when optimizer is disabled in config. */
  simulate?: boolean;
}

export interface UseFlowOptimizerOverlayResult {
  /** Preview fetch is allowed (simulate or optimizer enabled for this flow). */
  active: boolean;
  enabled: boolean;
  items: ReadonlyArray<FlowOptimizerItemPreview>;
  /** Campaign's SMSWiser country, for per-country flow-tag thresholds. */
  countryKey: string;
  /** Previewed campaign + tracker, for per-campaign flow-tag thresholds (none on Tres Commas). */
  campaignId?: string;
  provider?: TagTrafficProvider;
  isLoading: boolean;
}

export function useFlowOptimizerOverlay({
  flowId,
  campaignId,
  provider = 'voluum',
  simulate = false,
}: UseFlowOptimizerOverlayParams): UseFlowOptimizerOverlayResult {
  const idsReady = Boolean(campaignId && flowId);
  const config = useQuery({
    queryKey: ['flow-overlay-config', provider, campaignId, flowId],
    queryFn: () => getConfig(campaignId as string, flowId, provider),
    enabled: idsReady,
    staleTime: 60_000,
  });

  // The optimizer overlay (ranking + new weights + ⚡ tags) is the DEFAULT view:
  // it shows whenever this flow has a (non-killed) optimizer config — enabled or
  // not. `simulate` stays as an optional force that needs no config.
  const hasConfigForFlow = Boolean(
    config.data && config.data.flowId === flowId && !config.data.killSwitch,
  );
  const previewReady = Boolean(idsReady && (simulate || hasConfigForFlow));

  const preview = useQuery({
    queryKey: ['flow-overlay-preview', provider, campaignId, flowId, simulate],
    queryFn: () => previewOptimizer(campaignId as string, provider),
    enabled: previewReady,
    staleTime: 30_000,
  });

  return {
    active: previewReady,
    enabled: previewReady && Boolean(preview.data?.items?.length),
    items: preview.data?.items ?? [],
    countryKey: preview.data?.countryKey ?? '',
    campaignId: preview.data?.campaignId ?? campaignId,
    provider: preview.data?.provider ?? tagProviderOf(provider),
    isLoading:
      (idsReady && config.isLoading) ||
      (previewReady && (preview.isLoading || preview.isFetching)),
  };
}
